/**
 * Redux async actions (src/store/actions/teacherWallet.js).
 *
 * This module defines Redux Toolkit thunks (createAsyncThunk) that call backend endpoints via the shared axios client.
 * Slices consume the fulfilled payloads in `extraReducers` to update the UI state.
 */

import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../helpers/axiosconfig";
import { toast } from "react-toastify";
import { t } from "../../utils/i18nTranslate";

/**
 * Async thunk: setTeacherEarningRate.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const setTeacherEarningRate = createAsyncThunk(
  "teacherEarnings/setRate",
  async (data) => {
    try {
      const response = await axios.post(`admin/set-teacher-earnings`, data);
      toast.success(t("store.teacherWallet.rateSetSuccess"));
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || t("store.teacherWallet.setRateFailed"));
      throw error;
    }
  }
);

/**
 * Async thunk: creditLessonEarnings.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const creditLessonEarnings = createAsyncThunk(
  "teacherEarnings/creditLesson",
  async ({ bookingId, adminNotes }) => {
    try {
      const response = await axios.post(`lesson/complete/${bookingId}`, { adminNotes });
      toast.success(t("store.teacherWallet.lessonCompletedSuccess"));
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || t("store.teacherWallet.creditEarningsFailed"));
      throw error;
    }
  }
);

/**
 * Async thunk: getTeacherEarningsBalance.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getTeacherEarningsBalance = createAsyncThunk(
  "teacherEarnings/getBalance",
  async (teacherId) => {
    const response = await axios.get(`teacher/wallet/balance/${teacherId}`);
    return response.data;
  }
);

/**
 * Async thunk: getTeacherEarningsHistory.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getTeacherEarningsHistory = createAsyncThunk(
  "teacherEarnings/getHistory",
  async ({ teacherId, params }) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await axios.get(
      `teacher/wallet/transactions/${teacherId}?${queryString}`
    );
    return response.data;
  }
);


/**
 * Async thunk: fetchTeacherWalletData.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const fetchTeacherWalletData = createAsyncThunk(
    "teacherWallet/fetchData",
    async (teacherId, { rejectWithValue }) => {
      try {
        if (!teacherId) {
          throw new Error(t("store.teacherWallet.teacherIdRequired"));
        }
        const response = await axios.get(`teacher-bookings-with-earnings/${teacherId}`);
        return response.data;
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || t("store.teacherWallet.fetchDataFailed");
        toast.error(errorMessage);
        return rejectWithValue(errorMessage);
      }
    }
  );

/**
 * Async thunk: fetchTeacherWalletTransactions.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const fetchTeacherWalletTransactions = createAsyncThunk(
  "teacherWallet/fetchTransactions",
  async (teacherId, { rejectWithValue }) => {
    try {
      if (!teacherId) {
        throw new Error(t("store.teacherWallet.teacherIdRequired"));
      }
      const response = await axios.get(`teacher/wallet/transactions/${teacherId}`);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || t("store.teacherWallet.fetchTransactionsFailed");
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Async thunk: fetchTeacherWallet.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const fetchTeacherWallet = createAsyncThunk(
  "teacherWallet/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('teacher/wallet');
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || t("store.teacherWallet.fetchWalletFailed");
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Async thunk: fetchTeacherWalletStats.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const fetchTeacherWalletStats = createAsyncThunk(
  "teacherWallet/fetchStats",
  async (days, { rejectWithValue }) => {
    try {
      const response = await axios.get(`teacher/wallet/stats?days=${days}`);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || t("store.teacherWallet.fetchStatsFailed");
      return rejectWithValue(errorMessage);
    }
  }
);